import Image from 'components/FutureImage';
import { imageProxy } from 'tf-post-parser';
import VideoHeader from './VideoHeader';
import Link from 'next/link';
import Balancer from 'react-wrap-balancer';
import capitalize from 'helpers/capitalize';
import AuthorName from '../General/AuthorName';
import { fraunces } from '../Landing/fonts';
import { PlaneIcon } from '../Landing/ui';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Build-stable date label (no local Date math — these pages are SSG'd).
function dateLabel(createdAt) {
  const match = String(createdAt || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return `${MONTHS[Number(match[2]) - 1]} ${Number(match[3])}, ${match[1]}`;
}

// Header for internal /blog posts — same editorial language as the post
// page (chip, Fraunces title, mono byline, postcard cover), without the
// location machinery.
export default function SimpleHeader({
  title,
  coverImage,
  createdAt,
  youtubeId,
  tags,
  author,
}) {
  const date = dateLabel(createdAt);

  return (
    <div className={'tw-w-full ' + (youtubeId ? 'tw-mb-8' : 'tw-mb-4')}>
      <div className="tw-relative tw-mx-auto tw-max-w-3xl tw-px-4 tw-pb-4 tw-pt-2 tw-text-center">
        {tags && tags.length > 0 && (
          <div className="tw-mb-6 tw-flex tw-justify-center">
            <Link
              href={`/topics/${tags[0]}`}
              prefetch={false}
              className="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-solid tw-border-primary-600/25 dark:tw-border-primary-300/25 tw-bg-primary-600/[0.06] dark:tw-bg-primary-300/10 tw-px-3.5 tw-py-1.5 tw-font-mono tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-primary-800 dark:tw-text-primary-200 tw-no-underline tw-transition-colors hover:tw-bg-primary-600/[0.14]"
            >
              {capitalize(tags[0])}
            </Link>
          </div>
        )}
        <h1
          className={`${fraunces.className} tw-m-0 tw-text-[2.1rem] tw-font-semibold tw-leading-[1.1] tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] sm:tw-text-[3rem] sm:tw-leading-[1.06]`}
        >
          <Balancer>{title}</Balancer>
        </h1>
        <p className="tw-mb-0 tw-mt-5 tw-font-mono tw-text-[11px] tw-tracking-wider tw-text-[#8A958F]">
          {date}
          {author && (
            <>
              {' · '}
              <Link
                href={`/@${author.username}`}
                prefetch={false}
                className="tw-text-[#5F6F69] dark:tw-text-[#93A69E] tw-no-underline hover:tw-text-primary-700 dark:hover:tw-text-primary-300"
              >
                <AuthorName author={author} />
              </Link>
            </>
          )}
        </p>
        {/* Dashed route flourish */}
        <div
          aria-hidden="true"
          className="tw-mt-7 tw-flex tw-items-center tw-justify-center tw-gap-3"
        >
          <span className="tw-h-px tw-w-16 tw-border-0 tw-border-t tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] [border-top-width:2px]" />
          <PlaneIcon className="tw-h-4 tw-w-4 tw-rotate-90 tw-text-primary-600/60 dark:tw-text-primary-300/50" />
          <span className="tw-h-px tw-w-16 tw-border-0 tw-border-t tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] [border-top-width:2px]" />
        </div>
      </div>
      {youtubeId ? (
        <div className="tw-container tw-mx-auto tw-mt-6 tw-w-full tw-max-w-5xl tw-overflow-hidden tw-rounded-[20px] tw-bg-primary-200 dark:tw-bg-primary-700 tw-shadow-[0_30px_60px_-25px_rgba(29,45,40,0.4)]">
          <VideoHeader youtubeId={youtubeId} />
        </div>
      ) : (
        coverImage && (
          <div className="tw-container tw-mx-auto tw-mt-6 tw-h-56 tw-w-full tw-max-w-5xl tw-overflow-hidden tw-bg-gray-800 sm:tw-h-96 sm:tw-rounded-[20px] tw-shadow-[0_30px_60px_-25px_rgba(29,45,40,0.4)]">
            <div className="tw-relative tw-h-full tw-w-full">
              <Image
                priority
                className="tw-object-cover tw-object-center"
                src={imageProxy(coverImage.url, 2000, 800, 'cover')}
                alt={title}
                blurDataURL={coverImage.blur}
                placeholder={coverImage.blur ? 'blur' : undefined}
                fill
                sizes="(max-width: 680px) 100vw, (max-width: 1023px) 80vw, 62vw"
              />
            </div>
          </div>
        )
      )}
    </div>
  );
}
